
import { Component, Prop, Vue } from 'vue-property-decorator';
import { Action } from 'vuex-class';
import Project from '@/models/Project';
import ProjectShellComponent from '@/components/views/NewProjectView/ProjectShellComponent.vue';
import ProjectInternalOrders from '@/components/views/NewProjectView/Order/ProjectInternalOrders.vue';
import InternalOrdersTracker from '@/components/views/NewProjectView/Order/InternalOrdersTracker.vue';
import PdfListOverview from '@/components/views/NewProjectView/Order/PdfListOverview.vue';
import OrderGeneralInfo from '@/components/views/NewProjectView/Order/OrderGeneralInfo.vue';
import DownloadLeadPDF from '@/components/views/NewProjectView/Lead/DownloadLeadPDF.vue';
import { ProjectTabValues } from '@/enums/components/Project/ProjectTabValues';
import { EventBus } from '@/helpers/EventBusHelper';
import { EventBusEvents } from '@/enums/global/EventBusEvents';
import { ProjectStates } from '@/enums/components/Project/ProjectStates';
import { setFirstProjectOfferAsSelectedIfNoneIsSelected } from '@/helpers/NewProject/NewProjectHelper';
import OfferRepository from '@/repositories/OfferRepository';
import TechnologistOrderForm from '@/components/global/popup/TechnologistOrderForm.vue';
import { calculateLatestDate } from '@/helpers/OrderHelper';
import { formatDateToDateString } from '@/helpers/LocaleHelper';
import { PDFAttachment } from '@/interfaces/components/projectNew/InternalOrderPDFAttachment';
import { UserRightsMixin } from '@/mixins/UserRights';
import { mixins } from 'vue-class-component';
import moment from 'moment';
import RLogoIcon from '@/icons/RLogoIcon.vue';
import ProjectLogRepository from '@/repositories/ProjectLogRepository';

@Component({
    name: 'ProjectTabOrders',
    components: {
        ProjectShellComponent,
        DownloadLeadPDF,
        ProjectInternalOrders,
        PdfListOverview,
        OrderGeneralInfo,
        TechnologistOrderForm,
        InternalOrdersTracker,
        RLogoIcon,
    },
})
export default class ProjectTabOrders extends mixins<UserRightsMixin>(UserRightsMixin) {
    @Action('projectLoadingState/updateAreOffersLoading')
    private updateAreOffersLoading!: (newState: boolean) => void;
    @Prop({ required: true }) private projectId!: string;
    @Prop({ default: [] }) private loadedKeys!: Set<ProjectTabValues>;
    @Prop({ required: true }) private project!: Project;
    @Prop({ required: true }) private lockEditing!: boolean;
    @Prop({ required: false }) private isCurrentUserTechnologist!: boolean;
    @Prop({ required: false }) private isRoltekUG!: boolean;
    @Prop({ required: false }) private orderNumber!: string;

    private isLoaded: boolean = false;

    public get isProjectLocked() {
        if (this.project == null) {
            return false;
        }
        return this.project.state === ProjectStates.ORDER || this.lockEditing;
    }

    private get historyItems() {
        if (this.project == null) {
            return [];
        }

        return ProjectLogRepository.getAll();
    }

    private get showRoltekLogo() {
        return !this.isProjectImported && this.isOrderState;
    }

    private get selectedOffer() {
        return OfferRepository.getSelectedOffer(this.projectId);
    }

    private get isProjectImported() {
        return this.selectedOffer?.state?.toLocaleLowerCase() === 'imported';
    }

    // private get requestedDeliveryDate() {
    //     const date = this.selectedOffer?.requestedDeliveryDate?.date;

    //     if (!date) {
    //         return '-';
    //     }

    //     return date;
    // }

    private get isOrderState() {
        return this.project.state === ProjectStates.ORDER;
    }

    private get canSeeNewOrderOption() {
        return this.isCurrentUserTechnologist && this.canCreateTechnologistOrder;
    }

    private get dateTrackerTitle() {
        const lines = [
            this.$t('You can track your order using the table below.'),
            this.$t(
                'The table shows the steps of your order and can be extended to the right to see the progress of internal Roltek orders.'
            ),
            '',
            this.$t('The symbols have the following meaning:'),
            '⚪ – ' + this.$t("We are not here yet (the order hasn't reached this step)."),
            '🔵 – ' + this.$t('Some, but not all internal orders have reached this step.'),
            '🔵' +
                '<span style="position: absolute; left:1.2rem; z-index: 1">✓</span> – ' +
                this.$t('We are here, this step has been reached.'),
            this.$t(
                'If you have any questions or suggestions, please reach out to us an we will try to help or explain.'
            ),
        ];

        return lines.join('<br>');
    }

    private get internalOrders(): any[] {
        if (this.project?.projectInternalOrders == null) {
            return [];
        }

        return this.project.projectInternalOrders.map((order: any) => {
            const dates = order?.projectInternalOrderDates[0] || [];

            return {
                id: order?.id,
                orderNumber: order?.orderNumber ?? '-',
                offerCreated: formatDateToDateString(this.selectedOffer?.createdAt) || '-',
                orderCreated: formatDateToDateString(order?.createdAt) || '-',
                // orderAssigned: formatDateToDateString(dates?.technologistDate) || '-',
                orderSent: '-',
                orderConfirmation: formatDateToDateString(dates?.abConfirmedDate) || '-',
                // orderConfirmation: this.selectAvailableDate(dates),
                inProduction: formatDateToDateString(dates?.inProduction) || '-',
                orderProduction: formatDateToDateString(dates?.manufacturedDate) || '-',
                delivery: formatDateToDateString(dates?.deliveryDate) || '-',
                estimatedDeliveryDate: formatDateToDateString(dates?.delivery) || '-',
                deliveryDate:
                    formatDateToDateString(dates?.deliveryDate) || formatDateToDateString(dates?.delivery) || '-',
                system: 'RoltekApp',
                show: true,
                hasPDF: order?.attachments?.length > 0,
                attachment: order?.attachments[0] as PDFAttachment,
                isCompleted: order?.code === 'FINISHED',
            };
        });
    }

    private getLatestDate(statusName: string) {
        const dates = this.internalOrders.map((order: any) => {
            if (!order[statusName]) {
                return '';
            }

            return order[statusName];
        });

        return calculateLatestDate(dates);
    }

    // private get showDatesTracker() {
    //     const allowedUGs = ['Roltek', 'Roltek AT', 'Roltek HR'];

    //     return allowedUGs.includes(this.currentUserGroup?.name ?? '');
    // }

    private get generalInfoDates() {
        const deliveryDate = this.getLatestDate('deliveryDate');
        const manufactureDate = this.getLatestDate('orderProduction');

        const fallbackDeliveryDate =
            formatDateToDateString(this.selectedOffer?.requestedDeliveryDate) || '-';

        return {
            deliveryDate: deliveryDate && deliveryDate !== '-' ? deliveryDate : fallbackDeliveryDate,
            manufactureDate,
        };
    }

    private get allInternalOrdersHaveDeliveryDate() {
        if (!this.internalOrders.length) {
            return false;
        }

        return this.internalOrders.every((order) => order.delivery && order.delivery !== '-');
    }

    private get isManufactureDateInPast(): boolean {
        if (this.generalInfoDates.manufactureDate && this.generalInfoDates.manufactureDate !== '-') {
            try {
                const manufactureDate = moment(this.generalInfoDates.manufactureDate, 'DD.MM.YYYY');
                const today = moment();
                return manufactureDate.isBefore(today, 'day');
            } catch (e) {
                console.error('Error parsing manufacture date', e);
            }
        }
        return false;
    }

    private get manufactureDateTitle(): string {
        return this.isManufactureDateInPast ? 'Manufacture date' : 'Estimated manufacture date';
    }

    private get deliveryDateTitle(): string {
        if (!this.internalOrders.length) {
            return this.$t('Zahtevani datum dostave') as string;
        }

        const isValidDate = (dateStr: string | undefined): boolean => {
            if (!dateStr || dateStr === '-') return false;
            const year = parseInt(dateStr.substring(0, 4), 10);
            return !isNaN(year) && year >= 1970;
        };

        // at least one order has a real logistics deliveryDate
        const hasLogisticsDate = this.project?.projectInternalOrders?.some((order: any) => {
            const deliveryDate = order?.projectInternalOrderDates?.[0]?.deliveryDate;
            return isValidDate(deliveryDate);
        });

        if (hasLogisticsDate) {
            return this.$t('Datum dostave') as string;
        }

        // at least one order has a real technologist delivery (estimated)
        const hasTechnologistDate = this.project?.projectInternalOrders?.some((order: any) => {
            const delivery = order?.projectInternalOrderDates?.[0]?.delivery;
            return isValidDate(delivery);
        });

        if (hasTechnologistDate) {
            return this.$t('Datum predvidene dostave') as string;
        }

        // only client requested date, nothing confirmed yet
        return this.$t('Zahtevani datum dostave') as string;
    }

    private openOrderForm() {
        EventBus.$emit(EventBusEvents.showTechnologistOrderForm);
    }

    private async created() {
        if (this.loadedKeys.has(ProjectTabValues.Orders)) {
            this.isLoaded = true;
            return;
        }
        this.$emit('update:isTabLoading', true);
        try {
            setFirstProjectOfferAsSelectedIfNoneIsSelected(this.projectId);
            this.$emit('update:loadedKeys', new Set([...this.loadedKeys, ProjectTabValues.Orders]));
        } catch (e) {
            this.$notification.error({
                message: this.$t('Error'),
                description: this.$t('An error occurred while loading the project order data.'),
            });
        } finally {
            this.updateAreOffersLoading(false);
            this.$emit('update:isTabLoading', false);
            this.isLoaded = true;
        }
    }
}
